import { Component } from '@theme/component';

// The model worker and renderer are built from the offline Heart Studio source.
class PFHeartPreview extends Component {
  #controller; #observer; #intersection; #frame; #timer; #watchdog;
  #worker; #workerURL; #viewer; #loading; #seq = 0; #latest = 0;
  #complete = false; #settings; #geometryKey = ''; #colourKey = ''; #active = false; #lifecycle = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    const form = this.closest('form');
    if (!(form instanceof HTMLFormElement)) return;
    this.dataset.model = 'loading';
    this.querySelector('[data-pf-heart-canvas]').hidden = true;
    this.querySelector('[data-pf-heart-loading]').hidden = false;
    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    this.querySelector('[data-pf-heart-retry]')?.addEventListener('click', () => {
      this.#disposeModel();
      this.dataset.model = 'loading';
      this.querySelector('[data-pf-heart-retry]').hidden = true;
      this.querySelector('[data-pf-heart-loading]').hidden = false;
      this.#schedule();
    }, { signal });
    for (const name of ['input', 'change', 'reset']) form.addEventListener(name, event => {
      // Text input already invalidated and rebuilt on input. Its later blur/change
      // must not invalidate that confirmed model during the Add to cart click.
      if (name === 'change' && event.target instanceof HTMLInputElement && event.target.type === 'text') return;
      const saved = form.querySelector('[data-pf-heart-settings]');
      if (saved) saved.value = '';
      this.dataset.model = 'loading';
      this.#latest = ++this.#seq;
      this.#geometryKey = '';
      this.#schedule();
    }, { signal });
    this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(form, { attributes: true, attributeFilter: ['inert', 'aria-busy'] });
    this.#intersection = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        this.#active = true;
        this.#intersection.disconnect();
        this.#schedule();
      }
    }, { rootMargin: '150px' });
    this.#intersection.observe(this);
    this.#schedule();
  }

  updatedCallback() { super.updatedCallback(); this.#schedule(); }
  disconnectedCallback() {
    this.#controller?.abort(); this.#observer?.disconnect(); this.#intersection?.disconnect();
    cancelAnimationFrame(this.#frame); clearTimeout(this.#timer);
    this.#disposeModel(); this.#active = false;
    super.disconnectedCallback();
  }

  #disposeModel() {
    this.#lifecycle++;
    clearTimeout(this.#watchdog);
    this.#worker?.terminate(); this.#worker = null;
    if (this.#workerURL) URL.revokeObjectURL(this.#workerURL); this.#workerURL = null;
    this.#viewer?.dispose(); this.#viewer = null; this.#loading = null;
    this.#geometryKey = ''; this.#colourKey = '';
  }

  #schedule() { cancelAnimationFrame(this.#frame); this.#frame = requestAnimationFrame(() => this.#update()); }
  async #load() {
    if (this.#viewer) return;
    if (this.#loading) return this.#loading;
    const lifecycle = this.#lifecycle;
    this.#loading = (async () => {
      const [{ Viewer }, response] = await Promise.all([import(this.dataset.renderer), fetch(this.dataset.worker, { credentials: 'omit' })]);
      if (!response.ok) throw Error('Heart model unavailable');
      const code = await response.text();
      if (!this.isConnected || lifecycle !== this.#lifecycle) return;
      this.#workerURL = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
      this.#worker = new Worker(this.#workerURL);
      this.#viewer = new Viewer(this.querySelector('[data-pf-heart-canvas]'));
      this.#viewer.scene.background.set(getComputedStyle(this).getPropertyValue('--pf-brand-paper').trim() || '#fffaf0');
      this.#viewer.controls.enableZoom = false;
      this.#viewer.controls.enablePan = false;
      this.#viewer.controls.enableRotate = false;
      this.#worker.onmessage = ({ data }) => {
        if (!this.isConnected || data.id !== this.#latest) return;
        clearTimeout(this.#watchdog);
        if (data.type === 'error') { this.#fallback(); return; }
        try {
          this.#viewer.setModel(data.meshes, { ...data.settings, baseColor: this.#settings.base1, nameColor: this.#settings.letter1, logoColors: [this.#settings.letter1, this.#settings.base2, this.#settings.letter2] }, data.info);
        } catch { this.#fallback(); return; }
        this.querySelector('[data-pf-heart-canvas]').hidden = false;
        this.querySelector('[data-pf-heart-retry]').hidden = true;
        this.#colourKey = this.#colours(this.#settings);
        this.dataset.model = 'ready';
        const saved = this.closest('form')?.querySelector('[data-pf-heart-settings]');
        if (saved) saved.value = this.#complete ? JSON.stringify({format:'print-finland-heart-studio',version:1,profile:'pf-heart-store-2026-10-28.1',settings:{...data.heart,...this.#settings}}) : '';
        this.dataset.font = data.heart.font;
        this.dataset.hole = String(data.info.attachments?.[0]?.hole || data.heart.hole);
        this.dataset.shape = data.heart.shapeProfile;
        this.dataset.letterTop = data.heart.letterTop;
        this.dataset.thickness = data.info.thickness.toFixed(2);
      };
      this.#worker.onerror = () => this.#fallback();
    })().catch(() => { if (lifecycle === this.#lifecycle && this.isConnected) { this.#loading = null; this.#fallback(); } });
    return this.#loading;
  }

  #colours(s) { return JSON.stringify([s.base1, s.letter1, s.base2, s.letter2]); }
  #fallback() {
    clearTimeout(this.#watchdog);
    this.dataset.model = 'error'; this.#geometryKey = '';
    this.querySelector('[data-pf-heart-canvas]').hidden = true;
    this.querySelector('[data-pf-heart-retry]').hidden = false;
    this.querySelector('[data-pf-heart-loading]').hidden = true;
    this.querySelector('[data-pf-heart-status]').textContent = this.dataset.error;
  }
  #update() {
    const form = this.closest('form');
    if (!(form instanceof HTMLFormElement)) return;
    if (form.inert) return;
    let complete = true;
    const profile = form.querySelector('[data-pf-heart-profile]')?.value;
    const settings = profile === 'pf-heart-store-2026-10-28.1'
      ? { attachment: 'bore7', holeDetail: 'ramp', font: form.querySelector('[data-pf-heart-font]')?.value || 'rounded' }
      : profile === 'pf-heart-store-2026-10-27.2'
        ? { attachment: 'connector7', holeDetail: 'ramp' }
        : { attachment: 'custom', holeDetail: 'plain' };
    for (const piece of [1, 2]) {
      const initial = form.elements.namedItem(`properties[Name ${piece}]`);
      const value = initial instanceof HTMLInputElement ? initial.value.normalize('NFC').trim() : '';
      const valid = /^\p{L}$/u.test(value);
      complete &&= valid;
      const letter = valid ? value : (piece === 1 ? 'A' : 'M');
      settings[`initial${piece}`] = letter;
      for (const [property, key, fallback] of [
        [`Base colour ${piece}`, `base${piece}`, piece === 1 ? '#f4d2d5' : '#bce9f6'],
        [`Name colour ${piece}`, `letter${piece}`, '#f4f1e9'],
      ]) {
        const select = form.elements.namedItem(`properties[${property}]`);
        const option = select instanceof HTMLSelectElement && select.value ? select.selectedOptions[0] : null;
        const hex = option?.dataset.pfHex || '';
        const validColour = /^#[0-9a-f]{6}$/i.test(hex);
        complete &&= validColour;
        settings[key] = validColour ? hex : fallback;
      }
    }
    this.#complete = complete; this.#settings = settings;
    const status = this.querySelector('[data-pf-heart-status]');
    const message = complete ? this.dataset.ready : this.dataset.example;
    if (status && status.textContent !== message) status.textContent = message || '';
    if (!this.#active) return;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(async () => {
      await this.#load();
      if (!this.isConnected || !this.#worker || !this.#viewer) return;
      const current = this.#settings;
      const geometryKey = JSON.stringify([current.initial1, current.initial2, current.attachment, current.holeDetail, current.font]);
      if (this.#geometryKey === geometryKey) {
        const key = this.#colours(current);
        if (key !== this.#colourKey) {
          this.#viewer.colors(current.base1, current.letter1, [current.letter1, current.base2, current.letter2]);
          this.#colourKey = key;
        }
        return;
      }
      this.#geometryKey = geometryKey;
      this.#latest = ++this.#seq;
      clearTimeout(this.#watchdog);
      this.#watchdog = setTimeout(() => this.#fallback(), 20000);
      this.#worker.postMessage({ id: this.#latest, settings: current });
    }, 160);
  }
}
if (!customElements.get('pf-heart-preview')) customElements.define('pf-heart-preview', PFHeartPreview);

